import { ReactNode } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

interface Section {
  id: string;
  title: string;
}

interface LegalLayoutProps {
  title: string;
  description: string;
  lastUpdated: string;
  effectiveDate?: string;
  sections: Section[];
  children: ReactNode;
}

const LegalLayout = ({ title, description, lastUpdated, effectiveDate, sections, children }: LegalLayoutProps) => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="container mx-auto px-6 py-16 max-w-5xl">
        <header className="mb-10 pb-8 border-b border-border">
          <p className="text-xs uppercase tracking-widest text-primary mb-3 font-semibold">Legal</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">{title}</h1>
          <p className="text-muted-foreground text-lg max-w-3xl">{description}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground mt-6">
            <span>Last updated: {lastUpdated}</span>
            {effectiveDate && <span>Effective: {effectiveDate}</span>}
          </div>
        </header>

        <div className="grid md:grid-cols-[220px_1fr] gap-10">
          <aside className="md:sticky md:top-24 md:self-start" aria-label="Section navigation">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-3">On this page</p>
            <nav aria-label="On this page" className="flex flex-col gap-2 text-sm">
              {sections.map((s) => (
                <a key={s.id} href={`#${s.id}`} className="text-muted-foreground hover:text-primary transition-colors">
                  {s.title}
                </a>
              ))}
            </nav>
            <div className="mt-8 pt-6 border-t border-border text-xs text-muted-foreground space-y-2">
              <p className="font-semibold text-foreground">Related</p>
              <Link to="/privacy" className="block hover:text-primary">Privacy Policy</Link>
              <Link to="/terms" className="block hover:text-primary">Terms of Service</Link>
              <Link to="/cookies" className="block hover:text-primary">Cookie Policy</Link>
              <Link to="/acceptable-use" className="block hover:text-primary">Acceptable Use</Link>
            </div>
          </aside>

          <article className="prose prose-invert max-w-none prose-headings:font-display prose-headings:text-foreground prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3 prose-p:text-muted-foreground prose-p:leading-relaxed prose-li:text-muted-foreground prose-strong:text-foreground prose-a:text-primary prose-a:underline hover:prose-a:opacity-80 prose-code:text-foreground prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none">
            {children}
          </article>
        </div>

        <div className="mt-16 p-6 bg-card border border-border rounded-lg text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">Questions?</strong> Contact us at{" "}
            <a href="mailto:info@emrkt.co" className="text-primary underline">info@emrkt.co</a>.
            This policy is provided as-is and does not constitute legal advice.
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default LegalLayout;